import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import './index.less';

// 将颜色加深10%
const darkenColor = (color: string): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const darkerR = Math.max(0, Math.floor(r * 0.9));
  const darkerG = Math.max(0, Math.floor(g * 0.9));
  const darkerB = Math.max(0, Math.floor(b * 0.9));
  
  return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
};

interface OCTrack {
  hashValue: string;
  id: number;
  bpm: number;
  link: string;
  topColor?: string;
  bottomColor?: string;
}

interface ScanOcHashProps {
  hash: string;
  onCancel: () => void;
  onCollect: (info: { bpm: number }) => void;
}

const ScanOcHash: React.FC<ScanOcHashProps> = ({ hash, onCancel, onCollect }) => {
  const [track, setTrack] = useState<OCTrack | null>(null);
  const [bodySvg, setBodySvg] = useState<string>('');
  const [headSvg, setHeadSvg] = useState<string>('');
  const navigate = useNavigate();

  // 加载SVG文件
  useEffect(() => {
    fetch('https://oss.usecubes.cn/incredibox/assets/files/origin/original_0.svg')
      .then(response => response.text())
      .then(text => setBodySvg(text))
      .catch(error => console.error('Error loading body SVG:', error));

    fetch('https://oss.usecubes.cn/incredibox/assets/files/origin/original-0-head.svg')
      .then(response => response.text())
      .then(text => setHeadSvg(text))
      .catch(error => console.error('Error loading head SVG:', error));
  }, []);

  // 创建内联样式
  const createInlineStyle = () => {
    if (!track) return '';
    
    const topColor = track.topColor || '#ffffff';
    const bottomColor = track.bottomColor || '#ffffff';
    const topShadowColor = darkenColor(topColor);
    const bottomShadowColor = darkenColor(bottomColor);
    
    return `
      .hash-${track.hashValue} #head {
        fill: ${topColor} !important;
      }
      .hash-${track.hashValue} #head_shadow {
        fill: ${topShadowColor} !important;
      }
      .hash-${track.hashValue} #body {
        fill: ${bottomColor} !important;
      }
      .hash-${track.hashValue} #body_shadow {
        fill: ${bottomShadowColor} !important;
      }
    `;
  };

  useEffect(() => {
    if (hash) {
      const savedHashes = localStorage.getItem('scanedOcHash');
      if (savedHashes) {
        const hashList = JSON.parse(savedHashes);
        if (hashList.includes(hash)) {
          message.success('已收藏');
          onCollect({ bpm: track?.bpm || 0 });
          return;
        }
      }
    }
  }, [hash, onCollect, track]);

  useEffect(() => {
    if (hash) {
      fetch(`/api/v1/incredibox/oc/hash/${hash}`)
        .then(async response => {
          if (response.ok) {
            const data = await response.json();
            console.log(data);
            if (data && data.data) {
              setTrack(data.data);
            } else {
              message.warning('未找到相关数据');
              onCancel();
            }
          }
        })
        .catch(error => {
          message.error('获取数据失败');
          onCancel();
        });
    }
  }, [hash, onCancel]);

  const handleCollect = () => {
    if (hash) {
      const savedHashes = localStorage.getItem('scanedOcHash');
      let hashArray = savedHashes ? JSON.parse(savedHashes) : [];
      
      if (!hashArray.includes(hash)) {
        hashArray.push(hash);
        localStorage.setItem('scanedOcHash', JSON.stringify(hashArray));
        message.success('收藏成功');

      // 添加GA统计事件
      if (typeof gtag !== 'undefined') {
        gtag('event', 'collect_oc_track', {
          event_category: 'scan_oc_hash',
          event_label: hash,
          value: track?.bpm || 0
        });
      }
      }

      if (track) {
        onCollect({ bpm: track.bpm });
        navigate(`/?bpm=${track.bpm}&type=oc`);
      }
    }
  };

  if (!track) return null;

  return (
    <div className='scan-oc-hash-page'>
      <style>{createInlineStyle()}</style>
      <div className="track-info">
        <div className="thumb" style={{ backgroundImage: `url(/assets/images/avatar_bg.png)` }}>
          <div className={`thumb-svg hash-${track.hashValue}`}>
            <div className="thumb-svg-body" dangerouslySetInnerHTML={{ __html: bodySvg }} />
          </div>
        </div>
        <div className="infos">
          <div className="avatar">
            <div className={`avatar-svg hash-${track.hashValue}`}>
              <div dangerouslySetInnerHTML={{ __html: headSvg }} />
            </div>
          </div>
          <div className="track-name">{track.bpm} BPM</div>
        </div>
        <div className="track-actions">
          <button className="cancel-button" onClick={onCancel}>取消</button>
          <button className="collect-button" onClick={handleCollect}>+ 收藏</button>
        </div>
      </div>
    </div>
  );
};

export default ScanOcHash; 